// https://gist.github.com/samsonsprouse/71b764757b7b0e4fa259546d4fe975eb

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

// an accessible checkbox that uses a real but hidden checkbox and a
// label that uses styled psuedo-selectors to look like a checkbox
//
// Off state: square shape, grey border, white fill
// On state:  square shape, grey border, white fill, dark checkmark
const accessibleCheckboxLabelStyles = (props) => css`
  cursor: pointer;
  position: relative;
  display: inline-block;
  /* line-height of text should match height of checkbox  */
  line-height: ${`calc(${props.size} + 2px)`};
  /* space between checkbox and label */
  padding-left: ${`calc(${props.size} + 10px)`};

  &::before,
  &::after {
    display: inline-block;
    position: absolute;
    top: 0px;
    left: 0px;
    height: ${props.size};
    width: ${props.size};
    border: 1px solid #c3c3c3;
  }

  &::before {
    content: '';
  }

  &::after {
    text-align: center;
    font-size: ${`calc(${props.size} * .75)`};
    color: #999;
  }

  ${props.modifiers.map((func) => func(props))}
`;

const AccessibleCheckboxLabel = styled('label')`
  ${accessibleCheckboxLabelStyles};
`;

const InvisibleCheckbox = styled('input')`
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
  & + ${AccessibleCheckboxLabel}:after {
    content: none;
  }
  &:checked + ${AccessibleCheckboxLabel}:after {
    content: '';
    left: 7.5px;
    top: 3px;
    width: 5px;
    height: 10px;
    border: solid #999;
    border-width: 0 2px 2px 0;
    /* border-radius: 2px; */
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }
  &:checked + ${AccessibleCheckboxLabel}:before {
    background-color: #f1f1f1;
  }
  &:focus + ${AccessibleCheckboxLabel}:before {
    outline: auto 2px Highlight;
    outline: auto 5px -webkit-focus-ring-color;
  }
  &:active + ${AccessibleCheckboxLabel}:before {
    outline: auto 2px Highlight;
    outline: auto 5px -webkit-focus-ring-color;
  }
`;

// list of modifier functions to change the look of the checkbox
//
// Off state: round shape
// On state:  round shape
export const round = (props) => css`
  &::before {
    border-radius: 50%;
  }
  &::after {
    border-radius: 50%;
    font-size: ${`calc(${props.size} * .50)`};
    border: none;
  }
`;

// Off state: grey border, white fill
// On state:  no border, green fill, white checkmark
export const green = (props) => css`
  &::before {
    border: 1px solid #c3c3c3;
  }
  &::after {
    background-color: #7dbd31;
    color: #fff;
  }
`;

export const CheckboxAndLabel = styled('div')``;

const randomId = (len) =>
  [...Array(len)].map((_) => ((Math.random() * 36) | 0).toString(36)).join('');

export const AccessibleCheckbox = ({
  id,
  children,
  size,
  modifiers,
  ...rest
}) => {
  id = useMemo(() => id || randomId(8), []);
  return (
    <CheckboxAndLabel>
      <InvisibleCheckbox id={id} type="checkbox" {...rest} />
      <AccessibleCheckboxLabel
        id={id}
        htmlFor={id}
        size={size}
        modifiers={modifiers}
      >
        {children}
      </AccessibleCheckboxLabel>
    </CheckboxAndLabel>
  );
};

AccessibleCheckbox.propTypes = {
  size: PropTypes.string,
  modifiers: PropTypes.arrayOf(PropTypes.func),
  onChange: PropTypes.func.isRequired,
};

AccessibleCheckbox.defaultProps = {
  size: '20px',
  modifiers: [],
  onChange: () => {},
  children: <span />,
};
